import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import i18n from '@app/i18n';
import { instance } from '@app/api/axios';
import { fetchConfigs } from '@app/store/slices/configsSlice';
import { fetchTariff } from '@app/store/slices/tariffSlice';
import { fetchDeclineRate } from '@app/store/slices/declineRateSlice';
import {
  DEV_TOOLS_ENABLED,
  readDevOverrides,
  writeDevOverrides,
} from '@shared/static/devUserStates';

/* Локальные состояния пользователя для проверки интерфейса.
   Работает только при `npm start` и на стейдже, в проде игнорируется.
   Значения задаёт переключатель в сайдбаре, они живут в localStorage.

   group_role:    null Administrator | 1 Team lead | 2 Team member
   is_onboarding: 0 не пройдена | 1 пройдена | 2 на проверке
   is_frozen:     0 | 1          isFrozenGroup: false | true
   two_fa:        false | true                                  */

const initialState = {
  isInitialized: false,
  user: {},
  isFrozenGroup: false,
  isFrozen: false,
  isUnverified: true,
  apiUser: {},
  devOverrides: readDevOverrides(),
};

const calcFrozenGroup = (user) =>
  user?.groups?.length > 0 && user.groups.every((group) => group?.is_frozen);

const applyDevOverrides = (state, apiUser) => {
  if (!DEV_TOOLS_ENABLED) {
    state.user = apiUser;
    state.isFrozenGroup = calcFrozenGroup(apiUser);
  } else {
    const { isFrozenGroup, ...userFields } = state.devOverrides;

    state.user = { ...apiUser, ...userFields };
    state.isFrozenGroup =
      isFrozenGroup === undefined ? calcFrozenGroup(apiUser) : isFrozenGroup;
  }

  state.isFrozen = Boolean(state.user?.is_frozen || state.isFrozenGroup);
  state.isUnverified =
    !state.user?.is_onboarding || state.user.is_onboarding === 2;
};

export const fetchUser = createAsyncThunk(
  'user/fetchUser',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await instance.get('/user');
      i18n.changeLanguage(data.language);
      dispatch(fetchConfigs());
      dispatch(fetchTariff());
      dispatch(fetchDeclineRate());
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateUser: (state, action) => {
      state.apiUser = { ...state.apiUser, ...action.payload };
      applyDevOverrides(state, state.apiUser);
    },
    setDevOverride: (state, action) => {
      if (!DEV_TOOLS_ENABLED) return;
      const { field, value } = action.payload;

      if (value === undefined) {
        delete state.devOverrides[field];
      } else {
        state.devOverrides[field] = value;
      }

      writeDevOverrides(state.devOverrides);
      applyDevOverrides(state, state.apiUser);
    },
    resetDevOverrides: (state) => {
      if (!DEV_TOOLS_ENABLED) return;
      state.devOverrides = {};
      writeDevOverrides({});
      applyDevOverrides(state, state.apiUser);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.apiUser = action.payload;
        applyDevOverrides(state, state.apiUser);
        state.isInitialized = true;
      })
      .addCase(fetchUser.rejected, (state) => {
        state.user = {};
        state.apiUser = {};
        state.isFrozenGroup = false;
        state.isFrozen = false;
        state.isUnverified = true;
        state.isInitialized = false;
      });
  },
});

export const { updateUser, setDevOverride, resetDevOverrides } =
  userSlice.actions;

export default userSlice.reducer;
